import React, { useState, useEffect, useContext } from "react";
import DashboardLayout from "../../../layoutcontainers/DashboardLayout";
import { useNavigate, useSearchParams } from "react-router-dom";
import BASEURL from "../../Utils/Baseurl";
import axios from "axios";
import {
  Card,
  CardContent,
  CardHeader,
  Stack,
  Switch,
  FormControlLabel,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import MaterialTypography from "../../../components/MaterialTypography";
import MaterialButton from "../../../components/MaterialButton";
import MaterialInput from "../../../components/MaterialInput";
import MaterialSelect from "../../../components/MasterSelect";
import UserDetailsContext from "../../../context/auth";
import ConfirmationDialog from "../../../components/Dialog/ConfirmationDialog";
import Swal from "sweetalert2";
import {
  getShiftPaisaConfigurationById,
  createShiftPaisaConfigurationMutation,
  updateShiftPaisaConfigurationMutation,
  deleteShiftPaisaConfigurationMutation,
  employeeTypeOptionapi,
  allCategoriesQuery,
} from "./api";

const AddShiftPaisaConfiguration = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("id");
  const { user } = useContext(UserDetailsContext);

  const [togglePage, setTogglePage] = useState({ view: false, edit: false });
  const [employeeTypeOptions, setEmployeeTypeOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);

  const [configData, setConfigData] = useState({
    name: "",
    nameStatus: "unchecked",
    employeeType: "",
    employeeTypeStatus: "unchecked",
    category: "",
    categoryStatus: "unchecked",
    isActive: true,
    mustNotBeLate: true,
    minOtBeyondShiftMinutes: 210,
  });

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });

  useEffect(() => {
    fetchDropdowns();
    if (editId) {
      fetchConfigById(editId);
      setTogglePage({ view: true, edit: false });
    }
  }, [editId]);

  const fetchDropdowns = async () => {
    try {
      const [empRes, catRes] = await Promise.all([
        axios.post(BASEURL, { query: employeeTypeOptionapi }, { headers: { "Content-Type": "application/json" } }),
        axios.post(BASEURL, { query: allCategoriesQuery }, { headers: { "Content-Type": "application/json" } }),
      ]);

      const empTypes = empRes.data.data.allEmployeeTypes || [];
      setEmployeeTypeOptions(
        empTypes.map((e) => ({ value: e.employeetype, label: e.employeetype }))
      );

      const cats = catRes.data.data.allCategories || [];
      setCategoryOptions(
        cats.filter((c) => c && c.trim()).map((c) => ({ value: c, label: c }))
      );
    } catch (err) {
      console.error("Error fetching dropdowns:", err);
    }
  };

  const fetchConfigById = async (id) => {
    try {
      const response = await axios.post(
        BASEURL,
        { query: getShiftPaisaConfigurationById, variables: { id } },
        { headers: { "Content-Type": "application/json" } }
      );
      const data = response.data.data.shiftPaisaConfigurationById;
      if (data) {
        setConfigData({
          name: data.name || "",
          nameStatus: "success",
          employeeType: data.employeeType || "",
          employeeTypeStatus: "success",
          category: data.category || "",
          categoryStatus: "success",
          isActive: data.isActive !== false,
          mustNotBeLate: data.mustNotBeLate !== false,
          minOtBeyondShiftMinutes: data.minOtBeyondShiftMinutes || 210,
        });
      }
    } catch (err) {
      console.error("Error fetching config:", err);
    }
  };

  const handleChange = (field, value) => {
    let status = "unchecked";
    if (field === "name") {
      status = value.trim() ? "success" : "error";
    } else {
      status = "success";
    }
    setConfigData((prev) => ({
      ...prev,
      [field]: value,
      [`${field}Status`]: status,
    }));
  };

  const handleSubmit = () => {
    if (!configData.name.trim()) {
      Swal.fire("Error", "Configuration name is required.", "error");
      return;
    }

    setConfirmDialog({
      open: true,
      title: editId ? "Update Configuration" : "Create Configuration",
      message: editId
        ? "Are you sure you want to update this shift paisa configuration?"
        : "Are you sure you want to create this shift paisa configuration?",
      onConfirm: editId ? submitUpdate : submitCreate,
    });
  };

  const submitCreate = async () => {
    setConfirmDialog({ ...confirmDialog, open: false });
    try {
      const variables = {
        name: configData.name,
        employeeType: configData.employeeType || null,
        category: configData.category || null,
        isActive: configData.isActive,
        mustNotBeLate: configData.mustNotBeLate,
        minOtBeyondShiftMinutes: parseInt(configData.minOtBeyondShiftMinutes) || 210,
      };

      const response = await axios.post(
        BASEURL,
        { query: createShiftPaisaConfigurationMutation, variables },
        { headers: { "Content-Type": "application/json" } }
      );

      const result = response.data.data.createShiftPaisaConfiguration;
      if (result.success) {
        Swal.fire("Success", result.message, "success");
        navigate("/shift-paisa-configuration");
      } else {
        Swal.fire("Error", result.message, "error");
      }
    } catch (err) {
      console.error("Error creating config:", err);
      Swal.fire("Error", "Failed to create configuration.", "error");
    }
  };

  const submitUpdate = async () => {
    setConfirmDialog({ ...confirmDialog, open: false });
    try {
      const variables = {
        id: editId,
        name: configData.name,
        employeeType: configData.employeeType || null,
        category: configData.category || null,
        isActive: configData.isActive,
        mustNotBeLate: configData.mustNotBeLate,
        minOtBeyondShiftMinutes: parseInt(configData.minOtBeyondShiftMinutes) || 210,
      };

      const response = await axios.post(
        BASEURL,
        { query: updateShiftPaisaConfigurationMutation, variables },
        { headers: { "Content-Type": "application/json" } }
      );

      const result = response.data.data.updateShiftPaisaConfiguration;
      if (result.success) {
        Swal.fire("Success", result.message, "success");
        navigate("/shift-paisa-configuration");
      } else {
        Swal.fire("Error", result.message, "error");
      }
    } catch (err) {
      console.error("Error updating config:", err);
      Swal.fire("Error", "Failed to update configuration.", "error");
    }
  };

  const handleDelete = () => {
    setConfirmDialog({
      open: true,
      title: "Delete Configuration",
      message: "Are you sure you want to delete this shift paisa configuration? This action cannot be undone.",
      onConfirm: submitDelete,
    });
  };

  const submitDelete = async () => {
    setConfirmDialog({ ...confirmDialog, open: false });
    try {
      const response = await axios.post(
        BASEURL,
        {
          query: deleteShiftPaisaConfigurationMutation,
          variables: { id: editId },
        },
        { headers: { "Content-Type": "application/json" } }
      );

      const result = response.data.data.deleteShiftPaisaConfiguration;
      if (result.success) {
        Swal.fire("Deleted", result.message, "success");
        navigate("/shift-paisa-configuration");
      } else {
        Swal.fire("Error", result.message, "error");
      }
    } catch (err) {
      console.error("Error deleting config:", err);
      Swal.fire("Error", "Failed to delete configuration.", "error");
    }
  };

  const isViewMode = togglePage.view && !togglePage.edit;
  const isEditMode = togglePage.view && togglePage.edit;
  const isAddMode = !togglePage.view && !togglePage.edit;
  const isDisabled = isViewMode;

  const otHours = (parseInt(configData.minOtBeyondShiftMinutes) || 0) / 60;

  return (
    <DashboardLayout>
      <Card>
        <CardHeader
          title={
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <MaterialTypography variant="h5" fontWeight="medium">
                {isAddMode ? "Add Shift Paisa Configuration" : isEditMode ? "Edit Shift Paisa Configuration" : "Shift Paisa Configuration Details"}
              </MaterialTypography>
              <Stack direction="row" spacing={1}>
                {isViewMode && (
                  <>
                    <MaterialButton
                      variant="gradient"
                      color="dark"
                      size="small"
                      onClick={() => setTogglePage({ view: true, edit: true })}
                    >
                      Edit
                    </MaterialButton>
                    <MaterialButton
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={handleDelete}
                    >
                      Delete
                    </MaterialButton>
                  </>
                )}
                {isEditMode && (
                  <>
                    <MaterialButton
                      variant="gradient"
                      color="dark"
                      size="small"
                      onClick={handleSubmit}
                    >
                      Update
                    </MaterialButton>
                    <MaterialButton
                      variant="outlined"
                      color="dark"
                      size="small"
                      onClick={() => {
                        setTogglePage({ view: true, edit: false });
                        fetchConfigById(editId);
                      }}
                    >
                      Discard
                    </MaterialButton>
                  </>
                )}
                {isAddMode && (
                  <MaterialButton
                    variant="gradient"
                    color="dark"
                    size="small"
                    onClick={handleSubmit}
                  >
                    Submit
                  </MaterialButton>
                )}
              </Stack>
            </Stack>
          }
        />
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <MaterialInput
                label="Configuration Name"
                name="name"
                value={configData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                disabled={isDisabled}
                error={configData.nameStatus === "error"}
                success={configData.nameStatus === "success"}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <MaterialSelect
                label="Employee Type"
                name="employeeType"
                options={[{ value: "", label: "All" }, ...employeeTypeOptions]}
                value={
                  employeeTypeOptions.find((o) => o.value === configData.employeeType) ||
                  { value: "", label: "All" }
                }
                onChange={(selected) => handleChange("employeeType", selected?.value || "")}
                isDisabled={isDisabled}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <MaterialSelect
                label="Category"
                name="category"
                options={[{ value: "", label: "All" }, ...categoryOptions]}
                value={
                  categoryOptions.find((o) => o.value === configData.category) ||
                  { value: "", label: "All" }
                }
                onChange={(selected) => handleChange("category", selected?.value || "")}
                isDisabled={isDisabled}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={configData.isActive}
                    onChange={(e) =>
                      setConfigData((prev) => ({ ...prev, isActive: e.target.checked }))
                    }
                    disabled={isDisabled}
                    color="success"
                  />
                }
                label={configData.isActive ? "Active" : "Inactive"}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} mt={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <MaterialInput
                label={`Min OT Beyond Shift (minutes) = ${otHours.toFixed(1)} hrs`}
                name="minOtBeyondShiftMinutes"
                type="number"
                value={configData.minOtBeyondShiftMinutes}
                onChange={(e) =>
                  setConfigData((prev) => ({
                    ...prev,
                    minOtBeyondShiftMinutes: e.target.value,
                  }))
                }
                disabled={isDisabled}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={configData.mustNotBeLate}
                    onChange={(e) =>
                      setConfigData((prev) => ({ ...prev, mustNotBeLate: e.target.checked }))
                    }
                    disabled={isDisabled}
                    color="warning"
                  />
                }
                label="Must Not Be Late"
              />
              <MaterialTypography variant="caption" color="text">
                {configData.mustNotBeLate
                  ? "Late days are excluded from shift paisa"
                  : "Late days can still qualify for shift paisa"}
              </MaterialTypography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <ConfirmationDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        showConfirm={true}
        showCancel={true}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
      />
    </DashboardLayout>
  );
};

export default AddShiftPaisaConfiguration;
